import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, usePermissions } from '@hooks';
import Drawer from '@components/Drawer';
import Card from '@components/Card';
import { Typography } from '@components/Typography';
import { LinkedInIcon } from 'assets/images/integrations';
import { AccIntegrationName } from '@constants/integrations';
import { getActiveIntegrations, linkedinTargetLabelKey } from '@helpers/integrations';
import { handleApiError } from '@helpers/api';
import { alertActions, accountActions, locationsProfilesActions } from '@actions';
import { linkedinIntegrationService } from '@services';
import { RoutePath } from '@constants/routes';

// Drawer shown before a Location is wired to a LinkedIn author. Two selection axes: an account may have
// several LinkedIn logins connected (pick which one when there is more than one), and within a login the
// member can publish as their personal profile OR an organization Page they administer (pick the target).
const LinkedInIntegration = ({ onClose, onConnect, isOpen, selectedCompany, setSelectedCompany }) => {
    const { t } = useTranslation();
    const [targets, setTargets] = useState([]);
    const [selectedUrn, setSelectedUrn] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const account = useSelector(state => state.account.account);
    const { hasFullSettings } = usePermissions();
    const canManageIntegrations = hasFullSettings();

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const linkedinIntegrations = useMemo(() => getActiveIntegrations(account, AccIntegrationName.LinkedIn), [account]);
    const LINKEDIN_CONNECTED = linkedinIntegrations.length > 0;
    const hasMultipleAccounts = linkedinIntegrations.length > 1;

    const [selectedIntegrationId, setSelectedIntegrationId] = useState(null);

    useEffect(() => {
        if (isOpen && LINKEDIN_CONNECTED) {
            setSelectedIntegrationId(prev =>
                prev && linkedinIntegrations.some(i => i.id === prev) ? prev : linkedinIntegrations[0]?.id,
            );
        }
    }, [isOpen, LINKEDIN_CONNECTED, linkedinIntegrations]);

    useEffect(() => {
        // Targets are per-account: fetch the chosen LinkedIn login's personal profile and administered
        // Pages, and pre-select the first unconnected one.
        if (!isOpen || !selectedIntegrationId) return;
        setIsLoading(true);
        // Clear the previous account's targets so switching accounts never shows a stale login's
        // profile/Pages while the new fetch is in flight.
        setTargets([]);
        setSelectedUrn(null);
        linkedinIntegrationService
            .getTargets(selectedIntegrationId)
            .then(data => {
                const available = Array.isArray(data) ? data : [];
                setTargets(available);
                setSelectedUrn(available.find(target => !target.connected)?.urn ?? null);
            })
            .catch(() => setTargets([]))
            .finally(() => setIsLoading(false));
    }, [isOpen, selectedIntegrationId]);

    const goToIntegrations = () => navigate(RoutePath.AccountSettingsIntegrations);

    const onConnectLinkedIn = async () => {
        const target = targets.find(item => item.urn === selectedUrn);
        if (!target || !selectedIntegrationId) return;
        try {
            onClose();
            const updatedCompany = await linkedinIntegrationService.connectLocation(
                selectedCompany?.companyId,
                selectedIntegrationId,
                target,
            );
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        } finally {
            onConnect();
        }
    };

    const getPrimaryActionProps = () => {
        if (!LINKEDIN_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: goToIntegrations,
                primaryText: t('SettingsCompanies.goToIntegrations'),
                primaryDisabled: false,
            };
        }
        if (LINKEDIN_CONNECTED && canManageIntegrations) {
            return {
                primaryAction: onConnectLinkedIn,
                primaryText: t('buttons.connect'),
                primaryDisabled: !selectedUrn || !selectedIntegrationId || isLoading,
            };
        }
        return {
            primaryAction: goToIntegrations,
            primaryText: t('SettingsCompanies.goToIntegrations'),
            primaryDisabled: true,
        };
    };

    const subtitleFor = target => t(linkedinTargetLabelKey(target.type));

    const accountLabelFor = integration => integration.accountLabel || integration.userIdentifier || 'LinkedIn';

    return (
        <Drawer
            aboveModal
            anchor="right"
            open={isOpen}
            onClose={onClose}
            title={
                LINKEDIN_CONNECTED
                    ? t('SettingsCompanies.connectLocation')
                    : t('SettingsCompanies.connectDrawerNotConnected')
            }
            subtitle={
                LINKEDIN_CONNECTED
                    ? t('SettingsCompanies.connectDrawerDescription')
                    : t('SettingsCompanies.connectDrawerNotConnectedDescription')
            }
            {...getPrimaryActionProps()}
        >
            <div style={{ display: 'grid', gridGap: '8px' }}>
                {LINKEDIN_CONNECTED && hasMultipleAccounts && (
                    <>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                            {t('SettingsCompanies.selectAccountToLink')}
                        </Typography>
                        {linkedinIntegrations.map(integration => (
                            <Card
                                key={integration.id}
                                type="radio"
                                selected={selectedIntegrationId === integration.id}
                                title={accountLabelFor(integration)}
                                value={integration.id}
                                setValue={() => setSelectedIntegrationId(integration.id)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <LinkedInIcon width={20} height={20} />
                                    <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                        {t('SettingsCompanies.account')}
                                    </Typography>
                                </div>
                            </Card>
                        ))}
                    </>
                )}
                {LINKEDIN_CONNECTED &&
                    targets.map(target => (
                        <Card
                            key={target.urn}
                            type="radio"
                            selected={selectedUrn === target.urn}
                            disabled={target.connected}
                            title={target.name || subtitleFor(target)}
                            value={target.urn}
                            setValue={() => setSelectedUrn(target.urn)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {target.imageUrl ? (
                                    <img
                                        src={target.imageUrl}
                                        alt=""
                                        width={24}
                                        height={24}
                                        style={{ borderRadius: '50%' }}
                                    />
                                ) : (
                                    <LinkedInIcon width={20} height={20} />
                                )}
                                <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                                    {target.connected && target.connectedLocationName
                                        ? t('SettingsCompanies.connectedToLocation', {
                                              location: target.connectedLocationName,
                                          })
                                        : subtitleFor(target)}
                                </Typography>
                            </div>
                        </Card>
                    ))}
            </div>
        </Drawer>
    );
};

export default LinkedInIntegration;
